import React, {
  useEffect,
  useCallback,
  ChangeEvent,
  useRef,
  useMemo,
  useState,
} from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
  CountryWithStateList,
  stateList,
} from '../../../page-builder/data/country';
import { TextFieldControl } from '../../../page-builder/components/form-fields/TextField';
import {
  Select,
  SelectControl,
} from '../../../page-builder/components/form-fields/Select';
import {
  AddressFormType,
  IFieldOption,
} from '../../../page-builder/interface/formInterface';
import { loadGoogleAutoComplete } from '../../../page-builder/utils/loadGoogleAutoComplete';
import { ICountry, IProvince } from '../../interface/dataInterface';
import { getStatesForCountry } from '../../utils/checkStateRequirement';
import { PhoneNumberControl } from '../../../page-builder/components/form-fields/PhoneNumber';
import { ReduxSelectorProps } from '../../services/constants';
import './style.scss';
import {
  convertToSnakeCase,
  findClosestState,
  getBorderProps,
  getFontSize,
  getNewMarginStyles,
  getPaddingStyles,
  restrictEmojis,
} from '../../utils/helper';
import { IMaintainRowsProps } from './ShippingForm';
import { handleEventLogger } from '../../utils/conversionAPI';
import { Shop } from '../../interface/storeInterface';
import { useSearchParams } from 'react-router-dom';
import { Cart } from '../../interface/checkoutInterface';

interface AddressFormProps extends ReduxSelectorProps {
  fieldPrefix: string;
  placeholder?: Record<string, string>;
  option?: IFieldOption;
  maintainRows?: IMaintainRowsProps;
  currentDeviceType: string;
  currentDeviceTypes?: string[];
  fieldHeight?: string;
  rowGap?: string;
  colGap?: string;
  storeData: Shop;
  cartData: Cart | null;
}

export const checkStateRequirement = (
  country: string,
  countryCodes: string[],
) => {
  if (countryCodes.includes(country)) {
    return true;
  }
  return false;
};

export const AddressForm: React.FC<AddressFormProps> = ({
  fieldPrefix,
  currentDeviceType,
  option,
  fieldHeight,
  rowGap,
  colGap,
  maintainRows,
  storeData,
  cartData,
}) => {
  const address1FieldRef = useRef<HTMLInputElement>(null);
  const autoCompleteRef = useRef<google.maps.places.Autocomplete>();
  const [provinceCode, setProvinceCode] = useState<string>('');
  const restrictPOBox = option?.Address1?.restrictPOBox; // boolean
  const restrictMilitary = option?.Address1?.restrictMilitaryAddress; // boolean
  const hasInteractedRef = useRef(false);
  const [isLoadingAutocomplete, setIsLoadingAutocomplete] = useState(false);
  const [searchParams] = useSearchParams();
  const eventToken = searchParams.get('event-token');
  const {
    setValue,
    watch,
    control,
    formState: { errors },
  } = useFormContext<AddressFormType>();

  const selectedCountryStates: IProvince[] | undefined = useMemo(() => {
    const res = getStatesForCountry(
      CountryWithStateList || [],
      watch(`${fieldPrefix}.CountryCode`),
    );
    setValue(
      `${fieldPrefix}.ProvinceCode`,
      watch(`${fieldPrefix}.ProvinceCode`) || '',
    );
    const showStateIds = new Set(
      option?.CountryCode?.showOnlyState?.map(s => +s?.value),
    );

    const filteredProvinces = res?.provinces?.filter(
      item => item.id !== undefined && showStateIds.has(item.id),
    );

    return filteredProvinces?.length ? filteredProvinces : res?.provinces || [];
  }, [fieldPrefix, watch(`${fieldPrefix}.CountryCode`)]);

  const handleCountryChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setValue(`${fieldPrefix}.CountryCode`, event.target.value);
  };

  const fillInAddress = useCallback(() => {
    const autoComplete = autoCompleteRef.current;
    if (autoComplete) {
      const place = autoComplete.getPlace();
      let address = '';
      let city = '';
      let country = '';
      let state = '';
      let zip = '';

      for (const component of place.address_components ?? []) {
        const componentType = component.types[0];

        switch (componentType) {
          case 'street_number':
            address = `${component.long_name} ${address}`;
            break;

          case 'route':
            address += component.short_name;
            break;

          case 'locality':
            city = component.long_name;
            break;

          case 'political':
          case 'country':
            country = component.short_name;
            break;

          case 'administrative_area_level_1':
            state = component.short_name;
            break;

          case 'postal_code':
            zip = component.long_name;
            break;
        }
      }

      setValue(`${fieldPrefix}.Address1`, address, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue(`${fieldPrefix}.City`, city, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue(`${fieldPrefix}.CountryCode`, country, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue(`${fieldPrefix}.ProvinceCode`, state, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue(`${fieldPrefix}.Zip`, zip, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [setValue, autoCompleteRef, fieldPrefix]);

  useEffect(() => {
    const loadScript = async () => {
      try {
        await loadGoogleAutoComplete();
        waitForGoogleMapsPlaces(() => {
          if (
            address1FieldRef.current &&
            address1FieldRef.current.id === fieldPrefix
          ) {
            autoCompleteRef.current = new google.maps.places.Autocomplete(
              address1FieldRef.current,
              {
                fields: ['address_components', 'geometry'],
                types: ['address'],
              },
            );
            autoCompleteRef.current.addListener('place_changed', fillInAddress);
            setIsLoadingAutocomplete(false);
          }
        });
      } catch (error) {
        console.error(
          'Failed to load the Google Maps JavaScript API script. Error: ',
          error,
        );
        setIsLoadingAutocomplete(false);
      }
    };

    const handleUserInteraction = () => {
      if (!hasInteractedRef.current) {
        hasInteractedRef.current = true;
        loadScript();

        removeAllEventListeners();
      }
    };

    // Listen for any mouse interaction
    window.addEventListener('mousemove', handleUserInteraction);
    window.addEventListener('pointerdown', handleUserInteraction);
    window.addEventListener('keydown', handleUserInteraction);
    window.addEventListener('focus', handleUserInteraction, true);

    const removeAllEventListeners = () => {
      window.removeEventListener('mousemove', handleUserInteraction);
      window.removeEventListener('pointerdown', handleUserInteraction);
      window.removeEventListener('keydown', handleUserInteraction);
      window.removeEventListener('focus', handleUserInteraction, true);
    };

    // Cleanup in case component unmounts before interaction
    return () => removeAllEventListeners();
  }, [fieldPrefix, fillInAddress]);

  function waitForGoogleMapsPlaces(
    callback: () => void,
    attemptsLeft = 10,
    interval = 300,
  ) {
    if (typeof google !== 'undefined' && google.maps && google.maps.places) {
      callback();
    } else if (attemptsLeft === 0) {
      console.error(
        'Google Maps Places API failed to load within the expected time.',
      );
    } else {
      setTimeout(() => {
        waitForGoogleMapsPlaces(callback, attemptsLeft - 1, interval);
      }, interval);
    }
  }

  useEffect(() => {
    const isState = stateList?.find(
      state =>
        state?.name?.toLowerCase() === provinceCode?.trim()?.toLowerCase(),
    );
    let stateMatched = false;
    if (provinceCode && isState) {
      setValue(`${fieldPrefix}.ProvinceCode`, isState?.code, {
        shouldValidate: true,
        shouldDirty: true,
      });
      stateMatched = true;
    } else if (
      provinceCode &&
      !isState &&
      watch(`${fieldPrefix}.CountryCode`)
    ) {
      const countryID = CountryWithStateList.find(
        country => country.code === watch(`${fieldPrefix}.CountryCode`),
      )?.id;
      const isState = stateList?.find(
        state =>
          state?.code?.toLowerCase() ===
            provinceCode?.trim()?.toLowerCase()?.replace('string:', '') &&
          state?.country_id === countryID,
      );
      if (isState) {
        setValue(`${fieldPrefix}.ProvinceCode`, isState?.code, {
          shouldValidate: true,
          shouldDirty: true,
        });
        stateMatched = true;
      }
    }
    if (!stateMatched && provinceCode.length >= 3 && !isState) {
      const stateCode = findClosestState(provinceCode);
      if (stateCode) {
        setValue(`${fieldPrefix}.ProvinceCode`, stateCode, {
          shouldValidate: true,
          shouldDirty: true,
        });
        stateMatched = true;
      }
    }

    if (!stateMatched && provinceCode !== '') {
      handleEventLogger({
        eventType: 'shipping_address_autofill',
        shopifyDetails: { shopId: storeData?.ShopID },
        apiDetails: {
          response: {
            ProvinceState: provinceCode,
          },
          message: 'Province/State autofilled',
        },
        eventToken: eventToken || null,
        cartDetails: cartData || undefined,
      });
    }
  }, [provinceCode]);

  const getCountries = () => {
    const countriesList: ICountry[] = [];
    CountryWithStateList.forEach(country => {
      option?.CountryCode?.showOnlyCountry?.forEach(showCountry => {
        if (country.code === showCountry?.value) {
          countriesList.push(country);
        }
      });
    });
    return countriesList?.length ? countriesList : CountryWithStateList;
  };
  const addressClassName = convertToSnakeCase(
    option?.Address1?.placeholder || '',
  );
  return (
    <>
      <div
        className={`grid ${currentDeviceType === 'mobile' ? 'grid-cols-2' : 'grid-cols-1 sm:grid-cols-2'} font-light width-auto-form`}
        style={{
          gridRowGap: isNaN(Number(rowGap)) ? rowGap : Number(rowGap),
          gridColumnGap: isNaN(Number(colGap)) ? colGap : Number(colGap),
        }}>
        {option?.FirstName?.show && (
          <div
            className={`relative firstName sm:col-span-2 ${!maintainRows?.firstLastName?.[currentDeviceType] ? '' : 'col-span-full'}`}>
            <TextFieldControl
              id={`${fieldPrefix}.FirstName`}
              name={`${fieldPrefix}.FirstName`}
              uniqueName={`${fieldPrefix}FirstName`}
              header={option?.FirstName?.header}
              placeholder={option?.FirstName?.placeholder}
              radiusBorder={option?.FirstName?.border}
              outline={option?.FirstName?.outline}
              autoComplete="given-name"
              onChangeHandler={restrictEmojis}
              fieldHeight={fieldHeight}
              placeHolderColor={option?.FirstName?.color}
              textColor={option?.FirstName?.textColor}
              backgroundColor={option?.FirstName?.textBackgroundColor}
              placeHolderFontSize={option?.FirstName?.fontSize}
              placeHolderLineHeight={option?.FirstName?.lineHeight}
              PlaceHolderTextAlign={option?.FirstName?.textAlign}
              placeHolderweight={option?.FirstName?.fontWeight}
              placeHolderFontFamily={option?.FirstName?.fontFamily}
              placeHolderLetterSpace={option?.FirstName.letterSpace}
              currentDeviceType={currentDeviceType}
              rules={{
                required: option?.FirstName?.requiredValidation
                  ? option?.FirstName?.validationText ||
                    'First name is a required field'
                  : false,
              }}
            />
          </div>
        )}
        {option?.LastName?.show && (
          <div
            className={`${!maintainRows?.firstLastName?.[currentDeviceType] ? 'lastName' : 'col-span-full'}`}>
            <TextFieldControl
              id={`${fieldPrefix}.LastName`}
              name={`${fieldPrefix}.LastName`}
              uniqueName={`${fieldPrefix}LastName`}
              onChangeHandler={restrictEmojis}
              header={option?.LastName?.header}
              placeholder={option?.LastName?.placeholder}
              placeHolderColor={option?.LastName?.color}
              textColor={option?.LastName?.textColor}
              backgroundColor={option?.LastName?.textBackgroundColor}
              placeHolderFontSize={option?.LastName?.fontSize}
              placeHolderweight={option?.LastName?.fontWeight}
              radiusBorder={option?.LastName?.border}
              outline={option?.LastName?.outline}
              PlaceHolderTextAlign={option?.LastName?.textAlign}
              placeHolderLineHeight={option?.LastName?.lineHeight}
              placeHolderLetterSpace={option?.LastName.letterSpace}
              placeHolderFontFamily={option?.LastName?.fontFamily}
              currentDeviceType={currentDeviceType}
              autoComplete="family-name"
              fieldHeight={fieldHeight}
              rules={{
                required: option?.LastName?.requiredValidation
                  ? option?.LastName?.validationText ||
                    'Last name is a required field'
                  : false,
              }}
            />
          </div>
        )}
        {option?.Address1?.show && (
          <div
            className={`relative block w-full sm:col-span-2 text-editor-content ${currentDeviceType === 'mobile' ? 'col-span-2' : ''}`}>
            <Controller
              name={`${fieldPrefix}.Address1`}
              defaultValue={''}
              control={control}
              rules={{
                required: option?.Address1?.requiredValidation
                  ? option?.Address1?.validationText ||
                    'Address is a required field'
                  : false,
                validate: (value: string) => {
                  if (fieldPrefix === 'Shipping') {
                    if (restrictPOBox) {
                      const poBoxRegex =
                        /\b(?:P(?:\.|)?O(?:\.|)?\s*Box|Box\s+[A-Za-z0-9]+)/i;
                      if (poBoxRegex.test(value)) {
                        return 'Shipping to PO Boxes is not allowed for this shipping method.';
                      }
                    }

                    if (restrictMilitary) {
                      const militaryRegex = /\b(APO|FPO|DPO)\b/i;
                      if (militaryRegex.test(value)) {
                        return 'Shipping to military addresses is not allowed for this shipping method.';
                      }
                    }
                  }
                  return true;
                },
              }}
              render={({ field }) => {
                const [isInputFocused, setisInputFocused] = useState(false);
                const forbiddenSymbolsRegex = /[^\x20-\x7E]/g;

                const handlePaste = (
                  e: React.ClipboardEvent<HTMLInputElement>,
                ) => {
                  e.preventDefault();

                  const input = e.currentTarget;
                  const clipboardData = e.clipboardData.getData('text');
                  const sanitizedData = clipboardData.replace(
                    forbiddenSymbolsRegex,
                    '',
                  );

                  const start = input.selectionStart ?? 0;
                  const end = input.selectionEnd ?? 0;
                  const currentValue = input.value;

                  const newValue =
                    currentValue.slice(0, start) +
                    sanitizedData +
                    currentValue.slice(end);

                  field.onChange(newValue); // directly update form state

                  // Set cursor position after update
                  setTimeout(() => {
                    input.setSelectionRange(
                      start + sanitizedData.length,
                      start + sanitizedData.length,
                    );
                  }, 0);
                };

                return (
                  <>
                    {option?.Address1?.header &&
                      option?.Address1?.header.hide && (
                        <div
                          style={{
                            ...getPaddingStyles(
                              option?.Address1?.header.padding,
                              currentDeviceType || 'desktop',
                            ),
                            ...getNewMarginStyles(
                              option?.Address1?.header.margin,
                              currentDeviceType || 'desktop',
                            ),
                          }}
                          className="text-sm mb-1 ml-2"
                          dangerouslySetInnerHTML={{
                            __html: option?.Address1?.header.text,
                          }}
                        />
                      )}
                    <input
                      onKeyDown={e => e.stopPropagation()}
                      {...field}
                      onChange={e => {
                        const sanitized = e.target.value
                          .replace(/[^\x20-\x7E]/g, '')
                          .trimStart();
                        field.onChange(sanitized);
                      }}
                      onFocus={() => {
                        setisInputFocused(true);
                        if (!window.google || !window.google.maps?.places) {
                          setIsLoadingAutocomplete(true);
                        }
                      }}
                      onBlur={() => setisInputFocused(false)}
                      onPaste={handlePaste}
                      type="text"
                      style={{
                        height: fieldHeight
                          ? isNaN(+fieldHeight)
                            ? fieldHeight
                            : +fieldHeight
                          : '48px',
                        ...(isInputFocused && option?.Address1?.outline
                          ? getBorderProps(
                              option?.Address1?.outline,
                              currentDeviceType || 'desktop',
                            )
                          : getBorderProps(
                              option?.Address1?.border,
                              currentDeviceType || 'desktop',
                            )),
                        color: option?.Address1?.textColor?.isInherit
                          ? 'inherit'
                          : option?.Address1?.textColor?.color,
                        backgroundColor: option?.Address1?.textBackgroundColor
                          ?.isInherit
                          ? 'inherit'
                          : option?.Address1?.textBackgroundColor?.color,
                      }}
                      id={`${fieldPrefix}`}
                      className={`block w-full h-[42px] rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm custom-placeholder-${addressClassName} ${isInputFocused ? option?.Address1?.outline?.borderStyle?.[currentDeviceType || 'desktop'] : option?.Address1?.border?.borderStyle?.[currentDeviceType || 'desktop']}`}
                      ref={address1FieldRef}
                      placeholder={option?.Address1?.placeholder}
                    />
                  </>
                );
              }}
            />
            {isLoadingAutocomplete && (
              <div
                className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-md"
                style={{
                  padding: '8px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}>
                <div className="h-5 w-5 border-2 border-gray-700 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
            <style>
              {`
         .custom-placeholder-${addressClassName}::placeholder{
            color: ${option?.Address1?.color};
            font-size: ${getFontSize(option?.Address1?.fontSize)};
            font-weight:${option?.Address1?.fontWeight};
             height: ${option?.Address1?.lineHeight};
            text-align: ${option?.Address1?.textAlign};
            font-family: ${option?.Address1?.fontFamily};
            letter-spacing: ${option?.Address1?.letterSpace}px;
          }
          }
          `}
            </style>

            <div
              className="error-message "
              style={{ color: 'var(--error-color)' }}>
              {errors?.[fieldPrefix]?.Address1?.message}
            </div>
          </div>
        )}
        {option?.Address2?.show && (
          <TextFieldControl
            className={`sm:col-span-2 ${currentDeviceType === 'mobile' ? 'col-span-2' : ''}`}
            name={`${fieldPrefix}.Address2`}
            uniqueName={`${fieldPrefix}Address2`}
            onChangeHandler={restrictEmojis}
            header={option?.Address2?.header}
            placeholder={option?.Address2?.placeholder}
            placeHolderColor={option?.Address2?.color}
            placeHolderFontSize={option?.Address2?.fontSize}
            placeHolderweight={option?.Address2?.fontWeight}
            placeHolderLineHeight={option?.Address2?.lineHeight}
            PlaceHolderTextAlign={option?.Address2?.textAlign}
            textColor={option?.Address2?.textColor}
            backgroundColor={option?.Address2?.textBackgroundColor}
            placeHolderFontFamily={option?.Address2?.fontFamily}
            placeHolderLetterSpace={option?.Address2.letterSpace}
            fieldHeight={fieldHeight}
            radiusBorder={option?.Address2?.border}
            outline={option?.Address2?.outline}
            currentDeviceType={currentDeviceType}
            rules={{
              required: option?.Address2?.requiredValidation
                ? option?.Address2?.validationText ||
                  'Address is a required field'
                : false,
            }}
          />
        )}
        {option?.Organization?.show && (
          <TextFieldControl
            className={`sm:col-span-2 ${currentDeviceType === 'mobile' ? 'col-span-2' : ''}`}
            id={`${fieldPrefix}.Organization`}
            name={`${fieldPrefix}.Organization`}
            uniqueName={`${fieldPrefix}Organization`}
            onChangeHandler={restrictEmojis}
            header={option?.Organization?.header}
            placeHolderColor={option?.Organization?.color}
            placeHolderFontSize={option?.Organization?.fontSize}
            placeholder={option?.Organization?.placeholder}
            textColor={option?.Organization?.textColor}
            backgroundColor={option?.Organization?.textBackgroundColor}
            placeHolderweight={option?.Organization?.fontWeight}
            placeHolderFontFamily={option?.Organization?.fontFamily}
            PlaceHolderTextAlign={option?.Organization?.textAlign}
            placeHolderLetterSpace={option?.Organization.letterSpace}
            radiusBorder={option?.Organization?.border}
            outline={option?.Organization?.outline}
            currentDeviceType={currentDeviceType}
            fieldHeight={fieldHeight}
            rules={{
              required: option?.Organization?.requiredValidation
                ? option?.Organization?.validationText ||
                  'Organization is a required field'
                : false,
            }}
          />
        )}
        {option?.City?.show && (
          <TextFieldControl
            id={`${fieldPrefix}.City`}
            name={`${fieldPrefix}.City`}
            uniqueName={`${fieldPrefix}City`}
            onChangeHandler={restrictEmojis}
            header={option?.City?.header}
            className={`${!maintainRows?.countryProvince?.[currentDeviceType] ? 'col-span-1' : 'col-span-full'}`}
            placeHolderColor={option?.City?.color}
            placeHolderFontSize={option?.City?.fontSize}
            placeholder={option?.City?.placeholder}
            placeHolderweight={option?.City?.fontWeight}
            textColor={option?.City?.textColor}
            backgroundColor={option?.City?.textBackgroundColor}
            placeHolderFontFamily={option?.City?.fontFamily}
            PlaceHolderTextAlign={option?.City?.textAlign}
            placeHolderLetterSpace={option?.City.letterSpace}
            fieldHeight={fieldHeight}
            radiusBorder={option?.City?.border}
            outline={option?.City?.outline}
            currentDeviceType={currentDeviceType}
            rules={{
              required: option?.City?.requiredValidation
                ? option?.City?.validationText || 'City is a required field'
                : false,
            }}
          />
        )}
        {option?.CountryCode?.show && (
          <Controller
            name={`${fieldPrefix}.CountryCode`}
            rules={{
              required: option?.CountryCode?.requiredValidation
                ? option?.CountryCode?.validationText ||
                  'Country is a required field'
                : false,
            }}
            render={({
              field: { onChange, ...restField },
              fieldState: { error },
            }) => {
              return (
                <Select
                  options={getCountries()}
                  defaultOption={option?.CountryCode?.placeholder}
                  className={`${!maintainRows?.countryProvince?.[currentDeviceType] ? 'col-span-1' : 'col-span-full'}`}
                  header={option?.CountryCode?.header}
                  placeHolderColor={option?.CountryCode?.color}
                  placeHolderFontSize={option?.CountryCode?.fontSize}
                  placeHolderweight={option?.CountryCode?.fontWeight}
                  placeHolderLineHeight={option?.CountryCode?.lineHeight}
                  placeHolderFontFamily={option?.CountryCode?.fontFamily}
                  PlaceHolderTextAlign={option?.CountryCode?.textAlign}
                  textColor={option?.CountryCode?.textColor}
                  backgroundColor={option?.CountryCode?.textBackgroundColor}
                  placeHolderLetterSpace={option?.CountryCode.letterSpace}
                  fieldHeight={fieldHeight}
                  radiusBorder={option?.CountryCode?.border}
                  outline={option?.CountryCode?.outline}
                  currentDeviceType={currentDeviceType}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                    handleCountryChange(e);
                    setValue(`${fieldPrefix}.ProvinceCode`, '');
                    onChange(e.target.value);
                  }}
                  valueKey="code"
                  labelKey="name"
                  {...restField}
                  error={error?.message}
                />
              );
            }}
          />
        )}
        <input
          type="text"
          autoComplete="address-level1"
          id="state"
          className="p-0 w-1 h-1 absolute pointer-events-none"
          aria-hidden="true"
          tabIndex={-1}
          style={{ transform: 'translate3d(-9999px,0,0)' }}
          aria-label="State"
          onChange={e => setProvinceCode(e.target.value)}
        />
        {selectedCountryStates?.length > 0 && option?.ProvinceCode?.show && (
          <div
            className={`relative width-auto-state ${currentDeviceType === 'mobile' ? 'col-span-2' : ''}`}>
            <SelectControl
              name={`${fieldPrefix}.ProvinceCode`}
              options={selectedCountryStates}
              header={option?.ProvinceCode?.header}
              defaultOption={option?.ProvinceCode?.placeholder}
              placeHolderColor={option?.ProvinceCode?.color}
              textColor={option?.ProvinceCode?.textColor}
              placeHolderweight={option?.ProvinceCode?.fontWeight}
              PlaceHolderTextAlign={option?.ProvinceCode?.textAlign}
              placeHolderLineHeight={option?.ProvinceCode?.lineHeight}
              backgroundColor={option?.ProvinceCode?.textBackgroundColor}
              placeHolderFontSize={option?.ProvinceCode?.fontSize}
              placeHolderLetterSpace={option?.ProvinceCode.letterSpace}
              placeHolderFontFamily={option?.ProvinceCode?.fontFamily}
              fieldHeight={fieldHeight}
              radiusBorder={option?.ProvinceCode?.border}
              outline={option?.ProvinceCode?.outline}
              currentDeviceType={currentDeviceType}
              valueKey="code"
              labelKey="name"
              aria-label="State"
              rules={{
                required: option?.ProvinceCode?.requiredValidation
                  ? option?.ProvinceCode?.validationText ||
                    'State / Province is a required field'
                  : false,
              }}
            />
          </div>
        )}
        {option?.Zip?.show && (
          <div
            className={`relative width-auto-postal sm:col-span-2 ${currentDeviceType === 'mobile' ? 'col-span-2' : ''}`}>
            <TextFieldControl
              id={`${fieldPrefix}.postcode`}
              uniqueName={`${fieldPrefix}postcode`}
              header={option?.Zip?.header}
              autoComplete="postal-code"
              name={`${fieldPrefix}.Zip`}
              onChangeHandler={restrictEmojis}
              fieldHeight={fieldHeight}
              radiusBorder={option?.Zip?.border}
              outline={option?.Zip?.outline}
              placeHolderColor={option?.Zip?.color}
              textColor={option?.Zip?.textColor}
              backgroundColor={option?.Zip?.textBackgroundColor}
              placeHolderFontSize={option?.Zip?.fontSize}
              placeHolderweight={option?.Zip?.fontWeight}
              PlaceHolderTextAlign={option?.Zip?.textAlign}
              placeHolderLetterSpace={option?.Zip.letterSpace}
              placeHolderFontFamily={option?.Zip?.fontFamily}
              placeholder={option?.Zip?.placeholder}
              currentDeviceType={currentDeviceType}
              rules={{
                required: option?.Zip?.requiredValidation
                  ? option?.Zip?.validationText ||
                    'Postal code is a required field'
                  : false,
              }}
            />
          </div>
        )}

        {fieldPrefix === 'Shipping' && option?.Phone?.show && (
          <div
            className={`relative sm:col-span-2 ${currentDeviceType === 'mobile' ? 'col-span-2' : ''}`}>
            <PhoneNumberControl
              className=""
              fieldHeight={fieldHeight}
              radiusBorder={option?.Phone?.border}
              outline={option?.Phone?.outline}
              selectProps={{
                id: 'phone_code',
                autoComplete: 'phone_code',
              }}
              inputProps={{
                id: 'PhoneNumber',
                placeholder: option?.Phone?.placeholder,
              }}
              placeHolderColor={option?.Phone?.color}
              header={option?.Phone?.header}
              placeHolderFontSize={option?.Phone?.fontSize}
              textColor={option?.Phone?.textColor}
              backgroundColor={option?.Phone?.textBackgroundColor}
              PlaceHolderTextAlign={option?.Phone?.textAlign}
              placeHolderFontFamily={option?.Phone?.fontFamily}
              placeHolderLetterSpace={option?.Phone.letterSpace}
              placeHolderweight={option?.Phone?.fontWeight}
              currentDeviceType={currentDeviceType}
              phoneNumberName="CustomerData.PhoneNumber"
              phoneCodeName="phone_code"
              rules={{
                required: option?.Phone?.requiredValidation
                  ? option?.Phone?.validationText || 'Phone number is required'
                  : false,
              }}
            />
          </div>
        )}
      </div>
    </>
  );
};
